import { useEffect, useState } from "react";

import useComponentDidUpdateEffect from "@/Common/CustomHooks/useDidMountEffect";
import { Guid } from "@/Common/Models/Guid";
import { MedicalItemsTab, PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { MedicalSummaryPanelAction } from "@/Patient/Actions/MedicalSummaryPanelAction";
import { MedicalItemHelper } from "@/Patient/Helpers/MedicalItemHelper";
import MedicalSummaryPanelHelper from "@/Patient/Helpers/MedicalSummaryPanelHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { EnumPatientSummaryMedicationDisplayStatus } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationDisplayStatus";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicalSummaryPanelSettings } from "@/Patient/Models/Settings/MedicalSummaryPanelSettings";
import { MedicalItemViewOrigin } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { ClickableMedicalReactionItem } from "../MedicalItems/ClickableMedicalReactionItem";
import { ObstetricalSummaryComponent } from "../MedicalSummary/ObstetricalSummaryComponent";
import { ActiveMedicationSection } from "./Section/ActiveMedicationSection";
import { ActiveProblemAndConditionSection } from "./Section/ActiveProblemAndConditionSection";
import { FamilyHistorySection } from "./Section/FamilyHistorySection";
import { ImmunizationSection } from "./Section/ImmunizationSection";
import { PendingItemsSection } from "./Section/PendingItemsSection";
import { PreventiveActivitySection } from "./Section/PreventiveActivitySection";
import { SocialHabitsSection } from "./Section/SocialHabitsSection";
import { MedicalSummaryPanelContext } from "./MedicalSummaryPanelEnums";
import { MedicalSummaryPanelSection } from "./MedicalSummaryPanelSection";

import "./MedicalSummaryPanelAnimation.css";
import styles from "./MedicalSummaryPanel.module.scss";

interface IMedicalSummaryPanelProps {
    patientContactId: Guid;
    cssClass?: string;
    context: MedicalSummaryPanelContext;
    onMedicalSummaryLoaded?: () => void;
}

function MedicalSummaryPanel(props: IMedicalSummaryPanelProps): JSX.Element {
    const [panelState, setPanelState] = useState<MedicalSummaryPanelSettings>(MedicalSummaryPanelHelper.getSavedSettings(props.context));
    const [summaryModel, setSummaryModel] = useState(patientStore.PatientMedicalSummaryModels[props.patientContactId]);

    useComponentDidUpdateEffect(() => {
        saveUserSettings(panelState, props.context);
    }, [panelState, props.context]);

    useEffect(() => {
        let onChangeListenerId = null;

        if (props.context !== MedicalSummaryPanelContext.MedicalNote && props.context !== MedicalSummaryPanelContext.PatientLabTab) {
            loadSummaryWhenNotInPatientTab(props.patientContactId);
        }

        if (props.context === MedicalSummaryPanelContext.MedicalNote) {
            const changeListener = (): void => {
                setSummaryModel(patientStore.PatientMedicalSummaryModels[props.patientContactId]);
            };

            onChangeListenerId = patientStore.AddChangeListener(() => changeListener(), null, "medical-summary-panel-listener");
        }

        return () => {
            if (!IsNullOrUndefined(onChangeListenerId)) {
                patientStore.RemoveChangeListener(onChangeListenerId);
            }
        };
    }, [props.patientContactId, props.context]);

    useEffect(() => {
        setSummaryModel(patientStore.PatientMedicalSummaryModels[props.patientContactId]);
    }, [props.patientContactId]);

    const loadSummaryWhenNotInPatientTab = async (patientContactId: Guid): Promise<void> => {
        setSummaryModel(await MedicalSummaryPanelAction.loadPatientMedicalSummary(patientContactId));
        props.onMedicalSummaryLoaded?.();
    };

    const medicalSummaryModel: IPatientMedicalSummaryModel = IsNullOrUndefined(props.patientContactId) ? null : summaryModel;

    const filteredActiveProblems = MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(medicalSummaryModel?.get("MedicalItemsProblemsOnGoing").toArray()) as MedicalItemProblemAndCondition[];
    const filteredAllergies = MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(medicalSummaryModel?.get("MedicalItemsMedicalReaction").toArray()) as MedicalItemMedicalReaction[];
    const filteredActiveObstetricalProblems = MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(medicalSummaryModel?.get("MedicalItemsGynecologicalHistory").toArray()) as MedicalItemProblemAndCondition[];
    const filteredPsychiatricHistoryProblems = MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(medicalSummaryModel?.get("MedicalItemsPsychiatricHistory").toArray()) as MedicalItemProblemAndCondition[];
    const filteredSocialHabits = MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(medicalSummaryModel?.get("MedicalItemsRiskFactor").toArray()) as MedicalItemRiskFactor[];
    const filteredFamilyHistory = MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(medicalSummaryModel?.get("MedicalItemsFamilyHistory").toArray()) as MedicalItemFamilyHistory[];
    const filteredPreventiveActivities = filterPendingPreventiveActivities(medicalSummaryModel?.get("MedicalItemsScreeningTest"));
    const filteredLabRequests = filterPatientRequests(medicalSummaryModel?.get("Requests")) as RequestsInboxItem[];
    const filteredConsultationRequests = filterPatientRequests(medicalSummaryModel?.get("ConsultationRequests")) as ConsultationRequestsInboxItem[];
    const filteredSurgicalHistory = MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(medicalSummaryModel?.get("MedicalItemsSurgeryHistory").toArray()) as MedicalItemProblemAndCondition[];
    const allMedicationItems = medicalSummaryModel?.get("MedicalItemsMedicationAndPrescription").toArray()
        .filter((medication: MedicationModel) => (medication.Model.SummaryDisplayStatus !== EnumPatientSummaryMedicationDisplayStatus.Hide)) ?? [];
    const obstetricalSummary = medicalSummaryModel?.get("ObstetricalSummary");

    const activeMedications = allMedicationItems.sort((curr: MedicationModel, next: MedicationModel) => {
        let result = null;
        if (curr.Model.EMRTemplateSortIndex == next.Model.EMRTemplateSortIndex)
            result = 0;
        else
            result = (curr.Model.EMRTemplateSortIndex > next.Model.EMRTemplateSortIndex) ? 1 : -1;

        return result;
    });

    const filteredHistoryProblems = MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(medicalSummaryModel?.get("MedicalItemsProblemsHistory").toArray()) as MedicalItemProblemAndCondition[];
    const filteredImmunization = (MedicalItemHelper.getMedicalItemFromVisibleAndValidTemplate(
        medicalSummaryModel?.get("MedicalItemsImmunization").toArray()
    ) as MedicalItemImmunization[]).sortByProperty(immunization => immunization.CreationDate);

    const onMedicalItemClick = (model: any, viewMode: MedicalItemsTab): void => {
        patientStore.setActivePatientUI(props.patientContactId, false, () => {
            // That seems already done by the patient tab - are we loading CPP twice here ?
            patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                props.patientContactId,
                null,
                true,
                true,
                "MedicalSummaryPanel_ClickOnMedicalItem",
                () => {
                    switchToMedicalItemDetails(model, viewMode, props.context, props.patientContactId);
                }
            );
        }, true, false);

        PatientMedicalSummaryHelper.setMedicalItemPreviousRoute(viewMode, model.EMRTemplateMedicalItemSubType);
    };

    return (
        <div className={`${styles.summaryPanelContainer} ${styles[props.cssClass] ?? ""}`}>
            {MedicalItemScreeningTest.getLatestScreeningsToDisplay(medicalSummaryModel)?.any() && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsPreventiveActivitiesTabOpen: !panelState.IsPreventiveActivitiesTabOpen })}
                    isSectionOpen={panelState.IsPreventiveActivitiesTabOpen}
                    sectionTitle={cacheStore.GetString("PreventiveActivitiesLabel")}
                >
                    <PreventiveActivitySection
                        model={medicalSummaryModel}
                        onMedicalItemClick={(model, view: MedicalItemsTab) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}

            {(!isArrayNullOrUndefinedOrEmpty(filteredLabRequests) || !isArrayNullOrUndefinedOrEmpty(filteredConsultationRequests) || !isArrayNullOrUndefinedOrEmpty(filteredPreventiveActivities)) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsPendingItemsTabOpen: !panelState.IsPendingItemsTabOpen })}
                    isSectionOpen={panelState.IsPendingItemsTabOpen}
                    sectionTitle={cacheStore.GetString("PortalPendingItemsLabel")}
                    cssClass="blockItem"
                >
                    <PendingItemsSection
                        labRequests={filteredLabRequests}
                        consultationRequests={filteredConsultationRequests}
                        preventiveActivities={filteredPreventiveActivities}
                        onConsultationRequestItemClick={() => {}}
                        onLabRequestItemClick={() => {}}
                        onPreventiveActivitytemClick={() => {}}
                    />
                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(filteredActiveProblems) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsActiveProblemsTabOpen: !panelState.IsActiveProblemsTabOpen })}
                    isSectionOpen={panelState.IsActiveProblemsTabOpen}
                    sectionTitle={cacheStore.GetString("ProblemesActifsLabel")}
                    cssClass="blockItem"
                >
                    <ActiveProblemAndConditionSection
                        activeProblems={filteredActiveProblems}
                        onMedicalItemClick={(model, view: MedicalItemsTab) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(activeMedications) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsActiveMedicationsTabOpen: !panelState.IsActiveMedicationsTabOpen })}
                    isSectionOpen={panelState.IsActiveMedicationsTabOpen}
                    sectionTitle={cacheStore.GetString("ActiveMedicationLabel")}
                    cssClass="blockItem"
                >
                    <ActiveMedicationSection
                        activeMedication={activeMedications}
                        onMedicationItemClick={(model, view) => onMedicalItemClick(model, view)}
                    />

                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(filteredAllergies) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsAllergiesTabOpen: !panelState.IsAllergiesTabOpen })}
                    isSectionOpen={panelState.IsAllergiesTabOpen}
                    sectionTitle={cacheStore.GetString("AllergiesAndIntoleranceLabel")}
                >
                    <ClickableMedicalReactionItem
                        key="medical-reactions-in-medical-panel"
                        allergies={filteredAllergies}
                        onMedicalItemClick={(model, view: MedicalItemsTab) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(filteredHistoryProblems) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsMedicalHistoryTabOpen: !panelState.IsMedicalHistoryTabOpen })}
                    isSectionOpen={panelState.IsMedicalHistoryTabOpen}
                    sectionTitle={cacheStore.GetString("AntecedentsMedicauxLabel")}
                    cssClass="blockItem"
                >
                    <ActiveProblemAndConditionSection
                        activeProblems={filteredHistoryProblems}
                        onMedicalItemClick={(model, view: MedicalItemsTab) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(filteredSocialHabits) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsSocialHabitsTabOpen: !panelState.IsSocialHabitsTabOpen })}
                    isSectionOpen={panelState.IsSocialHabitsTabOpen}
                    sectionTitle={cacheStore.GetString("SocialHabitsLabel")}
                    cssClass="inlineItem"
                >
                    <SocialHabitsSection
                        socialHabits={filteredSocialHabits}
                        onSocialHabitClick={(model, view) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(filteredPsychiatricHistoryProblems) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsPsychiatricHistoryTabOpen: !panelState.IsPsychiatricHistoryTabOpen })}
                    isSectionOpen={panelState.IsPsychiatricHistoryTabOpen}
                    sectionTitle={cacheStore.GetString("AntecedentsPsychiatriquesLabel")}
                    cssClass="blockItem"
                >
                    <ActiveProblemAndConditionSection
                        activeProblems={filteredPsychiatricHistoryProblems}
                        onMedicalItemClick={(model, view: MedicalItemsTab) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(filteredSurgicalHistory) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsSurgicalHistoryTabOpen: !panelState.IsSurgicalHistoryTabOpen })}
                    isSectionOpen={panelState.IsSurgicalHistoryTabOpen}
                    sectionTitle={cacheStore.GetString("AntecedentsChirurgicauxLabel")}
                    cssClass="blockItem"
                >
                    <ActiveProblemAndConditionSection
                        activeProblems={filteredSurgicalHistory}
                        onMedicalItemClick={(model, view: MedicalItemsTab) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}

            {(!isArrayNullOrUndefinedOrEmpty(filteredActiveObstetricalProblems) || !!obstetricalSummary) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsObstetricalHistoryTabOpen: !panelState.IsObstetricalHistoryTabOpen })}
                    isSectionOpen={panelState.IsObstetricalHistoryTabOpen}
                    sectionTitle={cacheStore.GetString("AntecedentsObstetriquesLabel")}
                    cssClass="blockItem"
                >
                    {!!obstetricalSummary && (
                        <div className={styles.obstetricalSummary}>
                            <span className={styles.isPregnantLabel}>
                                {obstetricalSummary.IsPregnant ? cacheStore.GetString("PregnantLabel") : ""}
                            </span>
                            <ObstetricalSummaryComponent
                                obstetricalSummary={obstetricalSummary}
                            />
                        </div>
                    )}
                    {!isArrayNullOrUndefinedOrEmpty(filteredActiveObstetricalProblems) && (
                        <ActiveProblemAndConditionSection
                            activeProblems={filteredActiveObstetricalProblems}
                            onMedicalItemClick={(model, view: MedicalItemsTab) =>
                                onMedicalItemClick(model, view)
                            }
                        />
                    )}
                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(filteredImmunization) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsImmunizationsTabOpen: !panelState.IsImmunizationsTabOpen })}
                    isSectionOpen={panelState.IsImmunizationsTabOpen}
                    sectionTitle={cacheStore.GetString("ImmunizationsLabel")}
                    cssClass="inlineItem"
                >
                    <ImmunizationSection
                        immunizations={filteredImmunization}
                        onMedicalItemClick={(model, view: MedicalItemsTab) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}

            {!isArrayNullOrUndefinedOrEmpty(filteredFamilyHistory) && (
                <MedicalSummaryPanelSection
                    onSectionClick={() => setPanelState({ ...panelState, IsFamilyHistoryTabOpen: !panelState.IsFamilyHistoryTabOpen })}
                    isSectionOpen={panelState.IsFamilyHistoryTabOpen}
                    sectionTitle={cacheStore.GetString("FamilyHistoryLabel")}
                    cssClass="inlineItem"
                >
                    <FamilyHistorySection
                        familyHistory={filteredFamilyHistory}
                        onMedicalItemClick={(model: MedicalItemFamilyHistory, view: MedicalItemsTab) =>
                            onMedicalItemClick(model, view)
                        }
                    />
                </MedicalSummaryPanelSection>
            )}
        </div>
    );
}

function filterPatientRequests(requests: Immutable.List<RequestsInboxItem | ConsultationRequestsInboxItem>): (RequestsInboxItem | ConsultationRequestsInboxItem)[] {
    if (requests == null)
        return [];

    return requests
        ?.toArray()
        ?.filter(item =>
            item.StatusLookupId &&
            cacheStore.GetLookupById(item.StatusLookupId).LookupCode === "MedicalRequestStatus_PENDING" &&
            !item.IsInvalid
        );
}

function filterPendingPreventiveActivities(preventiveActivities: Immutable.List<MedicalItemScreeningTestResponse>): MedicalItemScreeningTestResponse[] {
    if (preventiveActivities == null)
        return [];

    return preventiveActivities
        ?.toArray()
        ?.filter(item =>
            item.StatusLookupId &&
            cacheStore.GetLookupById(item.StatusLookupId).LookupCode === "SCREENINGTESTSTATUS_PENDING" &&
            item.EMRTemplateIsVisibleInCPP &&
            !item.EMRTemplateIsInvalid
        );
}

function saveUserSettings(panelSettings: MedicalSummaryPanelSettings, context: MedicalSummaryPanelContext): void {
    switch (context) {
        case MedicalSummaryPanelContext.MedicalNote:
            MedicalSummaryPanelAction.updateMedicalSummaryPanelSettingsWithContext(
                panelSettings,
                cacheStore?.UserSettings?.MedicalNoteMedicalSummaryPanelSettings?.IsMedicalSummaryPanelOpen ?? false,
                MedicalSummaryPanelContext.MedicalNote
            );
            break;
        case MedicalSummaryPanelContext.PatientLabTab:
            MedicalSummaryPanelAction.updateMedicalSummaryPanelSettingsWithContext(
                panelSettings,
                cacheStore?.UserSettings?.LabViewMedicalSummaryPanelSettings?.IsMedicalSummaryPanelOpen ?? false,
                MedicalSummaryPanelContext.PatientLabTab
            );
            break;
        case MedicalSummaryPanelContext.ResultsTab:
            MedicalSummaryPanelAction.updateMedicalSummaryPanelSettingsWithContext(
                panelSettings,
                cacheStore?.UserSettings?.LabViewMedicalSummaryPanelSettings?.IsMedicalSummaryPanelOpen ?? false,
                MedicalSummaryPanelContext.ResultsTab
            );
            break;
        case MedicalSummaryPanelContext.PharmacyOrders:
            MedicalSummaryPanelAction.updateMedicalSummaryPanelSettingsWithContext(
                panelSettings,
                cacheStore?.UserSettings?.PharmacyOrdersMedicalSummaryPanelSettings?.IsMedicalSummaryPanelOpen ?? true,
                MedicalSummaryPanelContext.PharmacyOrders
            );
            break;
        default:
            break;
    }
}

function switchToMedicalItemDetails(model: any, viewMode: MedicalItemsTab, context: MedicalSummaryPanelContext, patientId: string): void {
    let operation;

    if (viewMode === MedicalItemsTab.Medication) {
        operation = new LocalEvent(LocalPatientOperations.UI.ChangeVerticalTabs);
        operation.AddItem("value", PatientTab.Medication);
        operation.AddItem("encounterId", model ? model.RelatedEncounterId : null);
        operation.AddItem("isPrescriptionTabActive", false);
        switch (context) {
            case MedicalSummaryPanelContext.DocumentsTab:
                operation.AddItem("MedicationViewOrigin", MedicalItemViewOrigin.DocumentsTab);
                break;
            case MedicalSummaryPanelContext.PatientDocumentsTab:
                operation.AddItem("MedicationViewOrigin", MedicalItemViewOrigin.PatientDocumentsTab);
                break;
            case MedicalSummaryPanelContext.PatientLabTab:
                operation.AddItem("MedicationViewOrigin", MedicalItemViewOrigin.PatientLabResultTab);
                break;
            case MedicalSummaryPanelContext.ResultsTab:
                operation.AddItem("MedicationViewOrigin", MedicalItemViewOrigin.ResultTab);
                break;
            case MedicalSummaryPanelContext.PharmacyOrders:
                operation.AddItem("MedicationViewOrigin", MedicalItemViewOrigin.PharmacyOrders);
                break;
            default:
                operation.AddItem("MedicationViewOrigin", MedicalItemViewOrigin.Note);
                break;
        }
    }
    else {
        switch (context) {
            case MedicalSummaryPanelContext.PatientLabTab:
                labsStore.Model = labsStore.Model.set("Action_MedicalItemDetails", true);
                operation = new LocalEvent(LocalPatientOperations.MedicalSummary.SwitchToDetails);
                operation.AddItem("MedicalItemViewOrigin", MedicalItemViewOrigin.PatientLabResultTab);
                break;
            case MedicalSummaryPanelContext.ResultsTab:
                operation = new LocalEvent(LocalPatientOperations.MedicalSummary.SwitchToDetails);
                operation.AddItem("MedicalItemViewOrigin", MedicalItemViewOrigin.ResultTab);
                break;
            case MedicalSummaryPanelContext.DocumentsTab:
                operation = new LocalEvent(LocalPatientOperations.MedicalSummary.SwitchToDetails);
                operation.AddItem("MedicalItemViewOrigin", MedicalItemViewOrigin.DocumentsTab);
                break;
            case MedicalSummaryPanelContext.PatientDocumentsTab:
                operation = new LocalEvent(LocalPatientOperations.MedicalSummary.SwitchToDetails);
                operation.AddItem("MedicalItemViewOrigin", MedicalItemViewOrigin.PatientDocumentsTab);
                break;
            case MedicalSummaryPanelContext.PharmacyOrders:
                operation = new LocalEvent(LocalPatientOperations.MedicalSummary.SwitchToDetails);
                operation.AddItem("MedicalItemViewOrigin", MedicalItemViewOrigin.PharmacyOrders);
                break;
            default:
                operation = new LocalEvent(LocalPatientOperations.MedicalNote.SwitchToDetails);
                operation.AddItem("MedicalItemViewOrigin", MedicalItemViewOrigin.Note);
                break;
        }

        operation.AddItem("viewMode", viewMode);
        operation.AddItem("activeTab", viewMode);
        operation.AddItem("selectedEMRTemplateId", model ? model.EMRTemplateId : "");
        operation.AddItem("PatientId", patientId);
    }

    LocalEventDispatcher.dispatch(operation);
}

export { MedicalSummaryPanel };
